import React, { Component, Fragment } from 'react';
import b from 'b_';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';

import { Publication } from '@interfaces/publications-page';
import Theme from '@interfaces/themes';
import { Researcher } from '@interfaces/researchers-page';
import { formatDate } from 'client/helpers/date';
import Button from '@components/button';
import Breadcrumbs from '@components/breadcrumbs';
import Share from '@components/share';
import PageMeta from '@components/page-meta';
import getFirstPublicationThemeWithImage from 'client/helpers/getFirstPublicationThemeWithImage';

import PublicationPageStub from './publication.stub';

import './publication.css';

export interface DispatchProps {
    fetchPublication(id: number): void;
}

export interface StateProps {
    data: Publication;
    isLoading: boolean;
}

interface State {
    displayShareBlock: boolean;
}

type Props = StateProps & DispatchProps & RouteComponentProps<{}>;

const class_ = b.with('publication-page');

class PublicationPage extends Component<Props, State> {
    public readonly state = {
        displayShareBlock: false
    };

    public componentDidMount() {
        const { fetchPublication, match, data } = this.props;
        const id = (match.params as any).id;

        if (!data || data.id !== parseInt(id, 10)) {
            fetchPublication(id);
        }
    }

    public shouldComponentUpdate() {
        return true;
    }

    public openShareBlock = () => {
        this.setState({ displayShareBlock: true });
    };

    public onDownload = () => {
        if (this.props.data.link) {
            window.open(this.props.data.link, '_blank');
        }
    };

    public renderButtons() {
        const { data } = this.props;
        const { displayShareBlock } = this.state;

        return (
            <div className={class_('buttons')}>
                {data.link && (
                    <Button
                        cls={`${class_('download')} button2_size_xl`}
                        onClick={this.onDownload}
                    >
                        Download
                    </Button>
                )}
                {displayShareBlock
                    ? <div className={class_('share-container')}><Share /></div>
                    : (
                        <Button
                            theme="action"
                            cls="button2_size_xl"
                            onClick={this.openShareBlock}
                        >
                            Share
                        </Button>
                    )
                }
            </div>
        );
    }

    /* tslint:disable-next-line */
    public render() {
        const { data, isLoading } = this.props;

        if (isLoading || !data || !data.id) {
            return <PublicationPageStub />;
        }

        const bg = getFirstPublicationThemeWithImage(data);
        const image = data.image || bg.previewImage || bg.image;

        const firstTheme = data.themes[0] || {};
        const year = formatDate(data.publicationDate as string, 'y');
        const breadcrumbs = [
            { text: 'Publications', url: '/publications' },
            { text: firstTheme.title, url: `/publications?themeSlug=${firstTheme.slug}` },
            { text: year, url: `/publications?themeSlug=${firstTheme.slug}&year=${year}` }
        ];

        return (
            <div className={class_()}>
                <PageMeta
                    title={`${data.title} - Publications`}
                    description={data.description}
                    image={image}
                />
                <div className={class_('header')} style={{ backgroundColor: bg.backgroundColor }}>
                    <Breadcrumbs cls="centering" items={breadcrumbs} hideHome />
                    <div className={class_('header-content')}>
                        <h1 className={class_('title')}>{data.title}</h1>
                        <div
                            className={class_('image')}
                            style={{ backgroundImage: `url(${image})` }}
                        />
                    </div>
                </div>
                <div className={class_('container')}>
                    <div className={class_('main-info')}>
                        {Boolean(data.description) && <div
                            className={class_('content')}
                            dangerouslySetInnerHTML={{ __html: data.description }}
                        />}
                        {this.renderButtons()}
                    </div>
                    <div className={class_('dependencies-info')}>
                        <div className={class_('params')}>
                            {Boolean(data.researchers.length) &&
                            <Fragment>
                                <div className={class_('param-title')}>Authors</div>
                                <div className={class_('param-list')}>
                                    {data.researchers.map(
                                        ({ id, firstName, lastName }: Researcher, index: number) =>
                                            (
                                                <Link key={index} to={`/people/${id}`}>
                                                    {firstName} {lastName}
                                                </Link>
                                            )
                                    )}
                                </div>
                            </Fragment>
                            }
                            {Boolean(data.themes.length) &&
                            <Fragment>
                                <div className={class_('param-title')}>Research areas</div>
                                <div className={class_('param-list')}>
                                    {data.themes.map(
                                        ({ slug, title }: Theme, index: number) =>
                                            (
                                                <Link key={index} to={`/publications?themeSlug=${slug}`}>
                                                    {title}
                                                </Link>
                                            )
                                    )}
                                </div>
                            </Fragment>
                            }
                            {Boolean(data.publicationPlace) &&
                            <Fragment>
                                <div className={class_('param-title')}>Published in</div>
                                <div className={class_('param-list')}>
                                    {Boolean(data.publicationPlaceUrl) ?
                                    (<a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={data.publicationPlaceUrl as string}
                                    >
                                        {data.publicationPlace}
                                    </a>) :
                                    data.publicationPlace
                                    }
                                </div>
                            </Fragment>
                            }
                            {Boolean(data.publicationDate) &&
                            <Fragment>
                                <div className={class_('param-title')}>Date</div>
                                <div className={class_('param-list')}>
                                    {formatDate(data.publicationDate as string, 'd LLL y')}
                                </div>
                            </Fragment>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(PublicationPage);
